import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useMemo } from 'react';
import { createEmptyActor } from 'xstate';
import { UserContext } from '~/app';
import { Tabs } from '~/components';
import '~/components/pageHeaders.css';
import { PROFILE_TABS } from './profileTabs';
import type { ProfileDrafts } from './useProfileDraftForm';
import { useSyncClerkNameOnProfileSave } from './useSyncClerkNameOnProfileSave';

const emptyActor = createEmptyActor();

export const ProfileForm = () => {
  const userRef = UserContext.useActorRef();
  const loading = useSelector(userRef, (state) => state.context.loading);

  // XState 5 system-registered actors are stored as AnyActorRef, losing type inference
  // through useSelector chains (userRef → accountRef → profileRef). The `any` casts are
  // required until a typed service hook (Service/View pattern) is extracted.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const vaultRef = useSelector(userRef, (state) => state.context.vaultRef) as any;

  const profileRef = useSelector(
    vaultRef ?? emptyActor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- See comment above, type inference lost through XState useSelector chains
    (state: any) => state?.context?.profileRef
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- See comment above, type inference lost through XState useSelector chains
  ) as any;

  const currentTab = (useSelector(
    profileRef ?? emptyActor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- See comment above, type inference lost through XState useSelector chains
    (state: any) => state?.context?.currentTab
  ) ?? 0) as number;

  useSyncClerkNameOnProfileSave(profileRef);

  const handleTabChange = (index: number) => {
    profileRef?.send({ type: 'ON_SET_ACTIVE_TAB', tab: index });
  };

  const safeTab = Math.max(0, Math.min(currentTab, PROFILE_TABS.length - 1));
  const tab = PROFILE_TABS[safeTab];
  const values = {};
  // Reset drafts if this page is reused for a different profile actor.
  const drafts = useMemo<ProfileDrafts>(
    () => ({
      personal: { current: undefined },
      sensitive: { current: undefined },
      professional: { current: undefined },
    }),
    [profileRef]
  );

  return (
    <div className="w-full px-0 py-0 sm:px-6 sm:py-6 lg:px-8">
      <div className="rounded-xl border border-grey-900 bg-white shadow-lg">
        <div className="px-4 py-4 sm:px-8 sm:py-6">
          <div className="mobile-page-header mb-5 border-b border-grey-900 pb-5 text-left">
            <h1 className="mb-2 text-3xl font-bold">Personal Profile & Demographics</h1>
            <p className="text-content-secondary">
              Please provide your personal, professional and contact information
            </p>
          </div>
          <div className="justify-start text-left">
            <div className="pb-4 sm:hidden">
              <label
                htmlFor="profile-section"
                className="mb-2 block text-sm font-semibold text-content-primary"
              >
                Profile section
              </label>
              <select
                id="profile-section"
                data-cy={CT.profile.tabs.sectionPicker}
                className="min-h-11 w-full rounded-md border border-grey-800 bg-grey-950 px-3 py-2 text-[1rem] text-content-primary focus:border-primary-50 focus:ring-3 focus:ring-primary-50/12"
                value={safeTab}
                onChange={(event) => handleTabChange(Number(event.target.value))}
              >
                {PROFILE_TABS.map((section, index) => (
                  <option key={section.cyTag} value={index}>
                    {section.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="hidden sm:block">
              <Tabs
                tabs={PROFILE_TABS.map((section, index) => ({
                  ...section,
                  current: index === safeTab,
                }))}
                handleTabChange={handleTabChange}
                activeTab={safeTab}
              />
            </div>
            <div className="pt-6">
              {!loading && profileRef && (
                <tab.form
                  key={profileRef.sessionId}
                  actor={profileRef}
                  values={values}
                  drafts={drafts}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
